'use client'

import { useState, useEffect, useRef } from 'react'
import { Share2, Copy } from 'lucide-react'
import { trackActivity } from '~/lib/activity-tracking'
import { env } from '~/env'

export default function EventShareButton({ eventUrl: eventUrlProp, eventId, accessToken }: { eventUrl: string; eventId?: string; accessToken?: string }) {
  const [isOpen, setIsOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  // #1305 — a denied clipboard permission (Safari, non-secure context,
  // iframe) used to show "Copied!" regardless, since the write was never
  // awaited/caught. Decided 2026-10-05 (Ed): show this instead.
  const [copyFailed, setCopyFailed] = useState(false)
  const popupRef = useRef<HTMLDivElement | null>(null)

  // Server call sites pass a relative path; absolutize with the origin at
  // render time (same convention as ShareQR — correct across dev/QA/prod with
  // zero per-environment config, NEXT_PUBLIC_WEB_URL as SSR fallback).
  const [origin, setOrigin] = useState('')
  useEffect(() => { setOrigin(window.location.origin) }, [])
  const eventUrl = eventUrlProp.startsWith('/')
    ? `${origin || env.NEXT_PUBLIC_WEB_URL || ''}${eventUrlProp}`
    : eventUrlProp

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopyFailed(true)
      setTimeout(() => setCopyFailed(false), 2000)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative inline-block" ref={popupRef}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          const opening = !isOpen
          setIsOpen(opening)
          if (opening && eventId) {
            trackActivity('EVENT_SHARE', 'EVENT', eventId, { accessToken, skipDedup: true })
          }
        }}
        className="p-2.5 rounded-full hover:bg-[#F7F0E6] transition"
        title="Share Event"
      >
        <Share2 className="h-5 w-5 text-[#5E5D59]" />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-60 bg-[#FFFDF8] border border-[#2C2D2B]/10 rounded-xl shadow-lg p-3 right-0">
          <p className="text-sm font-medium text-[#2C2D2B] mb-2 font-[family-name:var(--font-rounded)]">
            Share with friends
          </p>

          <div className="flex justify-around mb-2">
            {/* #1166 FR-6 — Messenger dropped: the dialog/send link has no
                app_id and never worked. */}

            {/* Instagram — copy link since IG doesn't support URL sharing.
                preventDefault + stopPropagation so the click can never bubble
                into a parent link and navigate to the event page. */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void handleCopy()
                window.open('https://www.instagram.com/', '_blank')
              }}
              className="p-2 rounded-full hover:bg-gray-100 transition"
              title="Copy link & open Instagram"
            >
              <img
                src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/instagram.svg"
                alt="Instagram"
                className="w-5 h-5"
              />
            </button>

            {/* WhatsApp */}
            <a
              href={`https://wa.me/?text=${encodeURIComponent(eventUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2 rounded-full hover:bg-gray-100 transition"
              title="Share on WhatsApp"
            >
              <img
                src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/whatsapp.svg"
                alt="WhatsApp"
                className="w-5 h-5"
              />
            </a>

            {/* LinkedIn */}
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(eventUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2 rounded-full hover:bg-gray-100 transition"
              title="Share on LinkedIn"
            >
              <img
                src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/linkedin.svg"
                alt="LinkedIn"
                className="w-5 h-5"
              />
            </a>
          </div>

          <div className="flex items-center border border-gray-200 rounded-lg px-2 py-1">
            <input
              readOnly
              value={eventUrl}
              className="text-xs text-gray-500 flex-grow bg-transparent outline-none truncate"
            />
            <button
              onClick={(e) => { e.stopPropagation(); void handleCopy() }}
              className="p-1 hover:bg-gray-100 rounded-md transition flex-shrink-0"
              title="Copy Link"
            >
              <Copy className="h-4 w-4 text-gray-600" />
            </button>
          </div>

          {copied && (
            <p className="text-[#4E7A6F] text-xs mt-1 text-right">Copied!</p>
          )}
          {copyFailed && (
            <p className="text-red-600 text-xs mt-1 text-right">
              Couldn&apos;t copy — select the link above
            </p>
          )}
        </div>
      )}
    </div>
  )
}
